import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useRef
} from "react";
import { Client, Message } from "stompjs";
import { ShowToast } from "./utils/showToast";

// Define the context interface
interface StompContextType {
  stompClient: Client | null;
  setStompClient: Dispatch<SetStateAction<Client | null>>;
  isConnected: boolean;
  setIsConnected: Dispatch<SetStateAction<boolean>>;
  output: string;
  send: (ongoingId: string, question: string) => void;
}

// Create context with default value as undefined
const StompContext = createContext<StompContextType | undefined>(undefined);

// Provider Props type
interface StompProviderProps {
  children: ReactNode;
}

export const StompAssessProvider: React.FC<StompProviderProps> = ({
  children
}) => {
  const [stompClient, setStompClient] = useState<Client | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [output, setOutput] = useState<string>("");
  const subscribedIdRef = useRef<string | null>(null);

  const send = (ongoingId: string, question: string) => {
    if (!stompClient || !isConnected) {
      // alert("Not connected!");
      ShowToast({
        severity: "error",
        label: "Failed to connect to test server",
        messageList: []
      });
      return;
    }

    if (!ongoingId || !question) {
      ShowToast({
        severity: "error",
        label: "Ongoing id/Question not found",
        messageList: []
      });
      return;
    }

    if (subscribedIdRef.current !== ongoingId) {
      subscribedIdRef.current = ongoingId;
      stompClient.subscribe(
        `/TESTCASE/OUTPUT/${ongoingId}`,
        (message: Message) => {
          setOutput((prev) => prev + "\n" + message.body);
        }
      );
    }

    stompClient.send(
      "/app/initiate/ongoing",
      {},
      JSON.stringify({ ongoingId, question })
    );
  };

  return (
    <StompContext.Provider
      value={{
        stompClient,
        setStompClient,
        isConnected,
        setIsConnected,
        output,
        send
      }}
    >
      {children}
    </StompContext.Provider>
  );
};

// Custom hook with error handling
export const useAssessStomp = (): StompContextType => {
  const context = useContext(StompContext);
  if (!context) {
    throw new Error("useAssessStomp must be used within a StompProvider");
  }
  return context;
};
